import { isResource } from '@medplum/core'
import type {
  Attachment,
  Binary,
  CommunicationRequest,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { codeableConcept } from '#src/codeableConcept.ts'
import { COMMUNICATION_REQUEST_CATEGORY_SYSTEM } from '#src/codeSystems.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import { createReference, getReferenceString } from '#src/references.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export const CCDA_EXPORT_CATEGORY = codeableConcept({
  system: COMMUNICATION_REQUEST_CATEGORY_SYSTEM,
  code: 'ccda-export',
  display: 'CCDA Export',
})

const CCDA_EXPORT_TITLE = 'CCDA Export'

export class CcdaCommunicationRequest extends ExtendedResource<CommunicationRequest> {
  static readonly RESOURCE_TYPE = 'CommunicationRequest'

  static isInstance(value: unknown): value is CommunicationRequest {
    return isResource(value) && isOfType(value, 'CommunicationRequest')
  }

  constructor(
    data: Omit<
      CommunicationRequest,
      'resourceType' | 'status' | 'category' | 'authoredOn'
    > & {
      status?: CommunicationRequest['status']
      category?: CommunicationRequest['category']
      ccdaBinary?: Binary
      ccdaTitle?: string
    }
  ) {
    const { status, category, ccdaBinary, ccdaTitle, ...rest } = data
    super({
      status: status ?? 'active',
      category: category ?? [CCDA_EXPORT_CATEGORY],
      authoredOn: new Date().toISOString(),
      ...rest,
    })

    if (ccdaBinary) {
      this.ccdaAttachment = {
        url: getReferenceString(ccdaBinary),
        contentType: 'application/cda+xml',
        title: ccdaTitle ?? CCDA_EXPORT_TITLE,
      }
    }
  }

  static bySubject(subject: Reference<Patient>): QueryTypes {
    return {
      subject: subject.reference,
      _sort: '-_lastUpdated',
      _count: '100',
      category: CCDA_EXPORT_CATEGORY,
    }
  }

  static forPatient(patientId: string): QueryTypes {
    return CcdaCommunicationRequest.bySubject(
      createReference({ resourceType: 'Patient', id: patientId })
    )
  }

  get ccdaAttachment(): Attachment | undefined {
    return this.resource.payload?.[0]?.contentAttachment
  }

  set ccdaAttachment(attachment: Attachment | undefined) {
    if (!attachment) {
      this.resource.payload = undefined
      return
    }
    this.resource.payload = [{ contentAttachment: attachment }]
  }

  get exportDate(): string | undefined {
    return this.resource.authoredOn
  }

  get requester(): Reference<Practitioner> | undefined {
    const ref = this.resource.requester
    if (ref) {
      invariant(
        isOfType(ref, 'Practitioner'),
        'Requester must be a Practitioner reference'
      )
    }
    return ref
  }
}
